import React, { useState, useEffect } from 'react';
import { Cloud, CloudRain, CloudSnow, Sun, CloudDrizzle, Zap, Wind, CloudFog, Thermometer, Droplets, MapPin, Navigation, Settings, X, Search, Moon } from 'lucide-react';

// Utility Functions
const interpretWeatherCode = (code) => {
  const weatherCodes = {
    0: { type: 'clear', description: 'Clear sky' },
    1: { type: 'clear', description: 'Mainly clear' },
    2: { type: 'clouds', description: 'Partly cloudy' },
    3: { type: 'clouds', description: 'Overcast' },
    45: { type: 'mist', description: 'Foggy' },
    48: { type: 'mist', description: 'Depositing rime fog' },
    51: { type: 'drizzle', description: 'Light drizzle' },
    53: { type: 'drizzle', description: 'Moderate drizzle' },
    55: { type: 'drizzle', description: 'Dense drizzle' },
    56: { type: 'drizzle', description: 'Freezing drizzle' },
    57: { type: 'drizzle', description: 'Freezing drizzle' },
    61: { type: 'rain', description: 'Slight rain' },
    63: { type: 'rain', description: 'Moderate rain' },
    65: { type: 'rain', description: 'Heavy rain' },
    66: { type: 'rain', description: 'Freezing rain' },
    67: { type: 'rain', description: 'Freezing rain' },
    71: { type: 'snow', description: 'Slight snow' },
    73: { type: 'snow', description: 'Moderate snow' },
    75: { type: 'snow', description: 'Heavy snow' },
    77: { type: 'snow', description: 'Snow grains' },
    80: { type: 'rain', description: 'Rain showers' },
    81: { type: 'rain', description: 'Rain showers' },
    82: { type: 'rain', description: 'Heavy rain showers' },
    85: { type: 'snow', description: 'Snow showers' },
    86: { type: 'snow', description: 'Heavy snow showers' },
    95: { type: 'thunderstorm', description: 'Thunderstorm' },
    96: { type: 'thunderstorm', description: 'Thunderstorm with hail' },
    99: { type: 'thunderstorm', description: 'Thunderstorm with hail' }
  };
  
  return weatherCodes[code] || { type: 'clear', description: 'Unknown' };
};

const getTimeOfDayInfo = (hours) => {
  // Dawn: 5-7am
  // Day: 7am-5pm
  // Dusk: 5-7pm
  // Night: 7pm-5am
  
  if (hours >= 5 && hours < 7) {
    return { period: 'dawn', isDark: false };
  } else if (hours >= 7 && hours < 17) {
    return { period: 'day', isDark: false };
  } else if (hours >= 17 && hours < 19) {
    return { period: 'dusk', isDark: false };
  } else {
    return { period: 'night', isDark: true };
  }
};

const getWeatherStyles = (weather, timeInfo) => {
  if (!weather) {
    return {
      gradient: 'from-blue-50 to-purple-50',
      cardBg: 'bg-white',
      handColor: '#1e293b',
      secondHandColor: '#ef4444',
      textColor: 'text-gray-800',
      dateColor: 'text-gray-500',
      icon: null,
      windEffect: ''
    };
  }

  const { condition, windSpeed } = weather;
  const { period, isDark } = timeInfo;
  const isWindy = windSpeed > 20;
  const windEffect = isWindy ? 'animate-pulse' : '';
  
  // Base styles for each weather condition with time variations
  const weatherStyles = {
    clear: {
      day: {
        gradient: 'from-yellow-100 via-orange-50 to-amber-100',
        cardBg: 'bg-gradient-to-br from-yellow-50 to-orange-50',
        handColor: '#92400e',
        secondHandColor: '#f59e0b',
        textColor: 'text-amber-900',
        dateColor: 'text-amber-600',
        icon: Sun,
        animation: 'animate-pulse'
      },
      dawn: {
        gradient: 'from-pink-200 via-orange-100 to-amber-200',
        cardBg: 'bg-gradient-to-br from-pink-100 to-orange-100',
        handColor: '#9f1239',
        secondHandColor: '#f97316',
        textColor: 'text-rose-900',
        dateColor: 'text-orange-600',
        icon: Sun,
        animation: ''
      },
      dusk: {
        gradient: 'from-orange-300 via-red-200 to-purple-300',
        cardBg: 'bg-gradient-to-br from-orange-200 to-purple-200',
        handColor: '#7c2d12',
        secondHandColor: '#dc2626',
        textColor: 'text-orange-900',
        dateColor: 'text-red-600',
        icon: Sun,
        animation: ''
      },
      night: {
        gradient: 'from-indigo-900 via-blue-900 to-slate-900',
        cardBg: 'bg-gradient-to-br from-indigo-950 to-slate-900',
        handColor: '#cbd5e1',
        secondHandColor: '#60a5fa',
        textColor: 'text-slate-200',
        dateColor: 'text-slate-400',
        icon: Moon,
        animation: 'animate-pulse'
      }
    },
    clouds: {
      day: {
        gradient: 'from-gray-200 via-slate-100 to-gray-300',
        cardBg: 'bg-gradient-to-br from-gray-100 to-slate-200',
        handColor: '#475569',
        secondHandColor: '#64748b',
        textColor: 'text-gray-700',
        dateColor: 'text-gray-500',
        icon: Cloud,
        animation: ''
      },
      dawn: {
        gradient: 'from-gray-300 via-slate-200 to-rose-200',
        cardBg: 'bg-gradient-to-br from-gray-200 to-slate-200',
        handColor: '#475569',
        secondHandColor: '#64748b',
        textColor: 'text-gray-700',
        dateColor: 'text-gray-500',
        icon: Cloud,
        animation: ''
      },
      dusk: {
        gradient: 'from-slate-400 via-gray-300 to-orange-200',
        cardBg: 'bg-gradient-to-br from-slate-300 to-gray-300',
        handColor: '#334155',
        secondHandColor: '#64748b',
        textColor: 'text-slate-800',
        dateColor: 'text-slate-600',
        icon: Cloud,
        animation: ''
      },
      night: {
        gradient: 'from-slate-800 via-gray-800 to-slate-900',
        cardBg: 'bg-gradient-to-br from-slate-800 to-gray-900',
        handColor: '#cbd5e1',
        secondHandColor: '#94a3b8',
        textColor: 'text-slate-200',
        dateColor: 'text-slate-400',
        icon: Cloud,
        animation: ''
      }
    },
    rain: {
      day: {
        gradient: 'from-blue-300 via-cyan-200 to-blue-400',
        cardBg: 'bg-gradient-to-br from-blue-100 to-cyan-100',
        handColor: '#1e3a8a',
        secondHandColor: '#3b82f6',
        textColor: 'text-blue-900',
        dateColor: 'text-blue-600',
        icon: CloudRain,
        animation: ''
      },
      dawn: {
        gradient: 'from-blue-400 via-slate-300 to-blue-400',
        cardBg: 'bg-gradient-to-br from-blue-200 to-slate-200',
        handColor: '#1e3a8a',
        secondHandColor: '#3b82f6',
        textColor: 'text-blue-900',
        dateColor: 'text-blue-700',
        icon: CloudRain,
        animation: ''
      },
      dusk: {
        gradient: 'from-blue-500 via-slate-400 to-indigo-500',
        cardBg: 'bg-gradient-to-br from-blue-300 to-slate-300',
        handColor: '#1e3a8a',
        secondHandColor: '#3b82f6',
        textColor: 'text-blue-900',
        dateColor: 'text-blue-700',
        icon: CloudRain,
        animation: ''
      },
      night: {
        gradient: 'from-blue-900 via-slate-900 to-blue-950',
        cardBg: 'bg-gradient-to-br from-blue-950 to-slate-900',
        handColor: '#93c5fd',
        secondHandColor: '#60a5fa',
        textColor: 'text-blue-200',
        dateColor: 'text-blue-400',
        icon: CloudRain,
        animation: ''
      }
    },
    drizzle: {
      day: {
        gradient: 'from-blue-200 via-gray-200 to-blue-300',
        cardBg: 'bg-gradient-to-br from-blue-50 to-gray-100',
        handColor: '#334155',
        secondHandColor: '#60a5fa',
        textColor: 'text-slate-800',
        dateColor: 'text-slate-500',
        icon: CloudDrizzle,
        animation: ''
      },
      night: {
        gradient: 'from-slate-800 via-gray-800 to-blue-900',
        cardBg: 'bg-gradient-to-br from-slate-900 to-blue-950',
        handColor: '#cbd5e1',
        secondHandColor: '#60a5fa',
        textColor: 'text-slate-200',
        dateColor: 'text-slate-400',
        icon: CloudDrizzle,
        animation: ''
      }
    },
    snow: {
      day: {
        gradient: 'from-white via-blue-50 to-gray-100',
        cardBg: 'bg-gradient-to-br from-white to-blue-50',
        handColor: '#334155',
        secondHandColor: '#60a5fa',
        textColor: 'text-slate-700',
        dateColor: 'text-slate-400',
        icon: CloudSnow,
        animation: 'animate-pulse'
      },
      night: {
        gradient: 'from-slate-700 via-blue-900 to-slate-800',
        cardBg: 'bg-gradient-to-br from-slate-800 to-blue-950',
        handColor: '#e2e8f0',
        secondHandColor: '#93c5fd',
        textColor: 'text-slate-100',
        dateColor: 'text-slate-300',
        icon: CloudSnow,
        animation: 'animate-pulse'
      }
    },
    thunderstorm: {
      day: {
        gradient: 'from-purple-400 via-gray-300 to-indigo-400',
        cardBg: 'bg-gradient-to-br from-purple-100 to-indigo-200',
        handColor: '#312e81',
        secondHandColor: '#7c3aed',
        textColor: 'text-purple-900',
        dateColor: 'text-purple-600',
        icon: Zap,
        animation: 'animate-bounce'
      },
      night: {
        gradient: 'from-purple-900 via-slate-900 to-indigo-950',
        cardBg: 'bg-gradient-to-br from-purple-950 to-indigo-950',
        handColor: '#c4b5fd',
        secondHandColor: '#a78bfa',
        textColor: 'text-purple-200',
        dateColor: 'text-purple-400',
        icon: Zap,
        animation: 'animate-bounce'
      }
    },
    mist: {
      day: {
        gradient: 'from-gray-300 via-gray-200 to-gray-300',
        cardBg: 'bg-gradient-to-br from-gray-50 to-gray-200',
        handColor: '#374151',
        secondHandColor: '#9ca3af',
        textColor: 'text-gray-700',
        dateColor: 'text-gray-400',
        icon: CloudFog,
        animation: ''
      },
      night: {
        gradient: 'from-gray-800 via-slate-800 to-gray-900',
        cardBg: 'bg-gradient-to-br from-gray-900 to-slate-900',
        handColor: '#d1d5db',
        secondHandColor: '#9ca3af',
        textColor: 'text-gray-200',
        dateColor: 'text-gray-400',
        icon: CloudFog,
        animation: ''
      }
    }
  };

  const weatherType = weatherStyles[condition] || weatherStyles.clouds;
  const timeStyle = weatherType[period] || weatherType[isDark ? 'night' : 'day'] || weatherType.day;
  
  return {
    ...timeStyle,
    windEffect
  };
};

// Custom Hooks
const useTime = () => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return time;
};

const useWeather = (location) => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${location.latitude}&longitude=${location.longitude}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m,precipitation&timezone=auto`
        );
        
        if (!response.ok) throw new Error('Weather fetch failed');
        
        const data = await response.json();
        const weatherCode = data.current.weather_code;
        const condition = interpretWeatherCode(weatherCode);
        
        setWeather({
          condition: condition.type,
          temp: Math.round(data.current.temperature_2m),
          humidity: data.current.relative_humidity_2m,
          windSpeed: Math.round(data.current.wind_speed_10m),
          precipitation: data.current.precipitation || 0,
          description: condition.description,
          city: location.name
        });
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching weather:', error);
        setWeather({
          condition: 'clear',
          temp: 20,
          humidity: 50,
          windSpeed: 10,
          precipitation: 0,
          description: 'Clear sky',
          city: location.name
        });
        setLoading(false);
      }
    };

    fetchWeather();
    const weatherInterval = setInterval(fetchWeather, 600000);
    return () => clearInterval(weatherInterval);
  }, [location]);

  return { weather, loading };
};

const useLocation = () => {
  const [location, setLocation] = useState({ 
    latitude: 52.52, 
    longitude: 13.41, 
    name: 'Biel' 
  });
  const [locationStatus, setLocationStatus] = useState('idle');
  const [locationError, setLocationError] = useState(null);
  const [searchingLocation, setSearchingLocation] = useState(false);

  const searchLocationByName = async (locationName) => {
    if (!locationName.trim()) return;
    
    setSearchingLocation(true);
    try {
      const response = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(locationName)}&count=1&language=en&format=json`
      );
      
      if (!response.ok) throw new Error('Location search failed');
      
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        const result = data.results[0];
        setLocation({
          latitude: result.latitude,
          longitude: result.longitude,
          name: result.name + (result.country ? `, ${result.country}` : '')
        });
        setLocationStatus('manual');
        setLocationError(null);
        return true;
      } else {
        setLocationError('Location not found. Try a different city name.');
        return false;
      }
    } catch (error) {
      console.error('Error searching location:', error);
      setLocationError('Failed to search location. Please try again.');
      return false;
    } finally {
      setSearchingLocation(false);
    }
  };

  const requestLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      setLocationStatus('denied');
      return;
    }

    setLocationStatus('requesting');
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        
        try {
          const geoResponse = await fetch(
            `https://geocoding-api.open-meteo.com/v1/reverse?latitude=${lat}&longitude=${lon}&language=en&count=1`
          );
          const geoData = await geoResponse.json();
          const cityName = geoData.results?.[0]?.city || 
                          geoData.results?.[0]?.name || 
                          'Your Location';
          
          setLocation({
            latitude: lat,
            longitude: lon,
            name: cityName
          });
        } catch (error) {
          setLocation({
            latitude: lat,
            longitude: lon,
            name: 'Your Location'
          });
        }
        
        setLocationStatus('granted');
      },
      (error) => {
        let errorMessage = 'Unable to get location';
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
          default:
            errorMessage = 'Unable to get location';
        }
        setLocationError(errorMessage);
        setLocationStatus('denied');
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  useEffect(() => {
    if (locationStatus === 'idle') {
      requestLocation();
    }
  }, []);

  return {
    location,
    locationStatus,
    locationError,
    searchingLocation,
    searchLocationByName,
    requestLocation
  };
};

// Components
const WeatherInfo = ({ weather, weatherStyle }) => {
  const WeatherIcon = weatherStyle.icon;

  return (
    <div className="space-y-2 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {WeatherIcon && (
            <WeatherIcon className={`w-6 h-6 ${weatherStyle.dateColor} ${weatherStyle.animation}`} />
          )}
          <span className={`text-sm ${weatherStyle.dateColor} capitalize`}>
            {weather.description}
          </span>
        </div>
        <div className={`text-lg font-medium ${weatherStyle.textColor} flex items-center gap-1`}>
          <Thermometer className="w-4 h-4" />
          {weather.temp}°C
        </div>
      </div>
      
      <div className="flex items-center justify-between text-xs">
        <div className={`flex items-center gap-1 ${weatherStyle.dateColor}`}>
          <Wind className={`w-3 h-3 ${weatherStyle.windEffect}`} />
          {weather.windSpeed} km/h
        </div>
        <div className={`flex items-center gap-1 ${weatherStyle.dateColor}`}>
          <Droplets className="w-3 h-3" />
          {weather.humidity}%
        </div>
        {weather.precipitation > 0 && (
          <div className={`flex items-center gap-1 ${weatherStyle.dateColor}`}>
            <CloudRain className="w-3 h-3" />
            {weather.precipitation}mm
          </div>
        )}
      </div>
    </div>
  );
};

const ClockFace = ({ time, weatherStyle }) => {
  const hours = time.getHours();
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();
  
  const hourDegrees = (hours % 12) * 30 + minutes * 0.5;
  const minuteDegrees = minutes * 6 + seconds * 0.1;
  const secondDegrees = seconds * 6;

  return (
    <div className="relative w-48 h-48 mx-auto mb-6">
      <svg className={`w-full h-full ${weatherStyle.windEffect}`}>
        <circle 
          cx="96" 
          cy="96" 
          r="94" 
          fill="none" 
          stroke={weatherStyle.handColor} 
          strokeWidth="0.5" 
          opacity="0.2"
        />
        
        {[...Array(12)].map((_, i) => {
          const angle = i * 30;
          const isMainHour = i % 3 === 0;
          const length = isMainHour ? 8 : 4;
          const x1 = 96 + (96 - length) * Math.cos((angle - 90) * Math.PI / 180);
          const y1 = 96 + (96 - length) * Math.sin((angle - 90) * Math.PI / 180);
          const x2 = 96 + 96 * Math.cos((angle - 90) * Math.PI / 180);
          const y2 = 96 + 96 * Math.sin((angle - 90) * Math.PI / 180);
          
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={weatherStyle.handColor}
              strokeWidth={isMainHour ? 2 : 1}
              opacity={isMainHour ? 0.6 : 0.3}
            />
          );
        })}
        
        <line
          x1="96"
          y1="96"
          x2={96 + 50 * Math.cos((hourDegrees - 90) * Math.PI / 180)}
          y2={96 + 50 * Math.sin((hourDegrees - 90) * Math.PI / 180)}
          stroke={weatherStyle.handColor}
          strokeWidth="4"
          strokeLinecap="round"
        />
        
        <line
          x1="96"
          y1="96"
          x2={96 + 70 * Math.cos((minuteDegrees - 90) * Math.PI / 180)}
          y2={96 + 70 * Math.sin((minuteDegrees - 90) * Math.PI / 180)}
          stroke={weatherStyle.handColor}
          strokeWidth="3"
          strokeLinecap="round"
        />
        
        <line
          x1="96"
          y1="96"
          x2={96 + 80 * Math.cos((secondDegrees - 90) * Math.PI / 180)}
          y2={96 + 80 * Math.sin((secondDegrees - 90) * Math.PI / 180)}
          stroke={weatherStyle.secondHandColor}
          strokeWidth="1"
          strokeLinecap="round"
        />
        
        <circle cx="96" cy="96" r="4" fill={weatherStyle.handColor} />
      </svg>
    </div>
  );
};

const TimeDisplay = ({ time, weather, weatherStyle }) => {
  const formatTime = () => {
    const hours = time.getHours();
    const minutes = time.getMinutes();
    const h = hours % 12 || 12;
    const m = minutes.toString().padStart(2, '0');
    const period = hours >= 12 ? 'pm' : 'am';
    return `${h}:${m}${period}`;
  };

  const formatDate = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayName = days[time.getDay()];
    const date = time.getDate();
    const month = months[time.getMonth()];
    
    const suffix = date === 1 || date === 21 || date === 31 ? 'st' :
                   date === 2 || date === 22 ? 'nd' :
                   date === 3 || date === 23 ? 'rd' : 'th';
    
    return `${dayName}, ${date}${suffix} ${month}`;
  };

  return (
    <div className="text-center">
      <div className={`text-3xl font-light ${weatherStyle.textColor}`}>
        {formatTime()}
      </div>
      <div className={`text-sm ${weatherStyle.dateColor} mt-1`}>
        {formatDate()}
      </div>
      {weather && (
        <div className={`text-xs ${weatherStyle.dateColor} mt-2 flex items-center justify-center gap-1`}>
          <MapPin className="w-3 h-3" />
          {weather.city}
        </div>
      )}
    </div>
  );
};

const SettingsPanel = ({ 
  showSettings, 
  setShowSettings, 
  location, 
  locationStatus,
  locationError,
  requestLocation,
  searchLocationByName,
  searchingLocation
}) => {
  const [manualLocation, setManualLocation] = useState('');

  const handleSearch = async () => {
    const success = await searchLocationByName(manualLocation);
    if (success) {
      setManualLocation('');
      setShowSettings(false);
    }
  };

  if (!showSettings) return null;

  return (
    <div className="absolute inset-0 bg-black bg-opacity-50 rounded-3xl z-20 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-xs shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Settings</h3>
          <button
            onClick={() => setShowSettings(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <p className="text-xs text-gray-600 mb-1">Current Location</p>
            <div className="flex items-center gap-1 text-sm text-gray-800">
              <MapPin className="w-3 h-3" />
              {location.name}
            </div>
          </div>

          <button
            onClick={requestLocation}
            disabled={locationStatus === 'requesting'}
            className="w-full px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-50"
          >
            <Navigation className={`w-4 h-4 ${locationStatus === 'requesting' ? 'animate-spin' : ''}`} />
            {locationStatus === 'requesting' ? 'Getting location...' : 'Use Current Location'}
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-white text-gray-500">OR</span>
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-600 mb-1 block">Enter City Name</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={manualLocation}
                onChange={(e) => setManualLocation(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                placeholder="e.g., Paris, Tokyo, New York"
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                disabled={searchingLocation}
              />
              <button
                onClick={handleSearch}
                disabled={searchingLocation || !manualLocation.trim()}
                className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Search className={`w-4 h-4 ${searchingLocation ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {locationError && (
            <div className="text-xs text-red-500 bg-red-50 rounded-lg p-2">
              {locationError}
            </div>
          )}

          {locationStatus === 'manual' && !locationError && (
            <div className="text-xs text-green-600 bg-green-50 rounded-lg p-2">
              Location updated successfully!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Component
const ClockComponent = () => {
  const time = useTime();
  const {
    location,
    locationStatus,
    locationError,
    searchingLocation,
    searchLocationByName,
    requestLocation
  } = useLocation();
  const { weather, loading } = useWeather(location);
  const [showSettings, setShowSettings] = useState(false);

  const timeInfo = getTimeOfDayInfo(time.getHours());
  const weatherStyle = getWeatherStyles(weather, timeInfo);

  return (
    <div className={`bg-gradient-to-br ${weatherStyle.gradient} rounded-3xl shadow-lg max-w-sm mx-auto transition-all duration-1000 relative`}>
      <button
        onClick={() => setShowSettings(!showSettings)}
        className={`absolute bottom-6 right-6 p-2 rounded-full ${weatherStyle.cardBg} shadow-md hover:shadow-lg transition-all duration-200 ${weatherStyle.textColor} z-10`}
      >
        <Settings className={`w-4 h-4 ${showSettings ? 'animate-spin' : ''}`} />
      </button>

      <SettingsPanel
        showSettings={showSettings}
        setShowSettings={setShowSettings}
        location={location}
        locationStatus={locationStatus}
        locationError={locationError}
        requestLocation={requestLocation}
        searchLocationByName={searchLocationByName}
        searchingLocation={searchingLocation}
      />

      <div className={`${weatherStyle.cardBg} rounded-2xl p-6 shadow-sm backdrop-blur-sm bg-opacity-90`}>
        {weather && !loading && (
          <WeatherInfo weather={weather} weatherStyle={weatherStyle} />
        )}
        
        <ClockFace time={time} weatherStyle={weatherStyle} />
        
        <TimeDisplay time={time} weather={weather} weatherStyle={weatherStyle} />
        
        {loading && !weather && (
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500 animate-pulse">Loading weather...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClockComponent;